// Reusable time-series line chart (SVG). Points are colored by capture view and
// connected within the same view; clicking a point jumps to that measurement.
// Pure presentational — no data access.

import type { GaitView } from "../../store/types";

export interface TrendPoint {
  t: number; // recordedAt (ms)
  v: number; // value on the chart's y-axis
  id: string; // sessionId (for click-through)
}

export interface TrendSeries {
  /** Gait series are colored by capture view. Non-gait callers (e.g. Flex) */
  /** can instead pass an explicit `color` and omit `view`. */
  view?: GaitView;
  color?: string;
  pts: TrendPoint[];
}

const VIEW_COLOR: Record<GaitView, string> = {
  lateral: "#7cc4ff",
  frontal: "#5eead4",
};

interface Props {
  series: TrendSeries[];
  min: number;
  max: number;
  height?: number;
  /** Optional formatter for the dot tooltip / value. */
  fmt?: (v: number) => string;
  onPick?: (id: string) => void;
}

export function TrendChart({ series, min, max, height = 150, fmt, onPick }: Props) {
  const W = 600;
  const H = height;
  const padL = 34, padR = 12, padT = 14, padB = 26;
  const allPts = series.flatMap((s) => s.pts);
  if (allPts.length === 0) return null;

  const ts = allPts.map((p) => p.t);
  const tMin = Math.min(...ts);
  const tMax = Math.max(...ts);
  const span = tMax - tMin || 1;
  const x = (t: number) => padL + ((t - tMin) / span) * (W - padL - padR);
  const y = (v: number) => padT + (1 - (v - min) / (max - min || 1)) * (H - padT - padB);

  const gridVals = [min, min + (max - min) / 2, max];
  const fmtV = fmt ?? ((v: number) => String(Math.round(v)));
  const dateLabel = (t: number) => new Date(t).toLocaleDateString(undefined, { day: "2-digit", month: "short" });

  return (
    <svg className="trend-chart" viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Verlauf">
      {/* y grid + labels */}
      {gridVals.map((g, i) => (
        <g key={i}>
          <line x1={padL} y1={y(g)} x2={W - padR} y2={y(g)} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          <text x={padL - 8} y={y(g) + 3} textAnchor="end" fontSize="10" fill="rgba(245,245,247,0.4)">
            {fmtV(g)}
          </text>
        </g>
      ))}
      {/* x end labels */}
      <text x={padL} y={H - 8} textAnchor="start" fontSize="10" fill="rgba(245,245,247,0.4)">{dateLabel(tMin)}</text>
      {tMax !== tMin && (
        <text x={W - padR} y={H - 8} textAnchor="end" fontSize="10" fill="rgba(245,245,247,0.4)">{dateLabel(tMax)}</text>
      )}

      {series.map((s, si) => {
        const col = s.color ?? (s.view ? VIEW_COLOR[s.view] : "#0a84ff");
        const pts = [...s.pts].sort((a, b) => a.t - b.t);
        const line = pts.map((p, i) => `${i ? "L" : "M"}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join("");
        return (
          <g key={s.view ?? si}>
            {pts.length > 1 && (
              <path d={line} fill="none" stroke={col} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
            )}
            {pts.map((p) => (
              <circle
                key={p.id}
                cx={x(p.t)}
                cy={y(p.v)}
                r="5"
                fill="#0b0b0d"
                stroke={col}
                strokeWidth="2.5"
                style={{ cursor: onPick ? "pointer" : "default" }}
                onClick={() => onPick?.(p.id)}
              >
                <title>{`${dateLabel(p.t)}${s.view ? ` · ${s.view === "frontal" ? "frontal" : "seitlich"}` : ""} · ${fmtV(p.v)}`}</title>
              </circle>
            ))}
          </g>
        );
      })}
    </svg>
  );
}
